import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/site';
import { getOrganizationSchema, getWebSiteSchema, getWebPageSchema, getBreadcrumbSchema } from '@/utils/structuredData';

const milestones = [
  { year: '2024年3月', event: '团队成立，聚焦网络内容安全教育领域' },
  { year: '2024年6月', event: '官网正式上线，发布首批风险警示系列文章' },
  { year: '2024年9月', event: '与3家省级网信办建立内容合作，文章阅读量突破100万' },
  { year: '2025年1月', event: '启动"清朗校园"公益项目，覆盖100所中小学' },
  { year: '2025年6月', event: '累计发布原创内容200+篇，覆盖用户超过500万' },
  { year: '2026年1月', event: '荣获"最佳网络安全教育平台"奖项' },
];

const partners = [
  { name: '中国互联网协会', role: '战略合作单位' },
  { name: '北京师范大学', role: '学术研究合作' },
  { name: '12321举报中心', role: '信息同步合作' },
  { name: '抖音安全中心', role: '内容安全合作' },
  { name: '腾讯安全', role: '技术安全顾问' },
  { name: '中国青少年网络协会', role: '青少年保护合作' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('mobile_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    formData.delete('mobile_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9sr42llbrt1qcgaj8jg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后再试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试。');
      setFormStatus('error');
    }
  };

  const aboutJsonLd = [
    getOrganizationSchema(),
    getWebSiteSchema(),
    getWebPageSchema(`关于我们 - ${siteInfo.fullName}`, siteInfo.description, `${siteInfo.url}/about`),
    getBreadcrumbSchema([
      { name: '首页', item: siteInfo.url },
      { name: '关于我们', item: `${siteInfo.url}/about` },
    ]),
  ];

  return (
    <Layout>
      <SEO
        title={`关于我们 - ${siteInfo.fullName}`}
        description={`了解${siteInfo.name}的品牌故事、使命愿景、发展历程和联系方式。${siteInfo.description}`}
        keywords="关于我们,品牌介绍,联系方式,茄子视频污,网络安全教育,不良视频危害"
        canonicalUrl={`${siteInfo.url}/about`}
        jsonLd={aboutJsonLd}
      />

      <div className="pt-20 bg-background-50">
        <div className="relative w-full h-[300px] md:h-[380px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20professional%20team%20collaboration%20concept%20with%20warm%20amber%20and%20soft%20cream%20tones%2C%20modern%20minimalist%20office%20atmosphere%2C%20clean%20geometric%20shapes%2C%20no%20people%2C%20no%20text%2C%20professional%20and%20trustworthy%20feeling&width=1920&height=600&seq=about-hero&orientation=landscape"
            alt="关于我们"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6 pt-16">
              <div className="max-w-4xl">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-3">
                  关于我们
                </h1>
                <p className="text-background-50/80 text-sm md:text-base max-w-xl">
                  致力于中国网络空间清朗化，守护亿万网民的数字健康
                </p>
              </div>
            </div>
          </div>
        </div>

        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                品牌使命
              </h2>
              <div className="bg-accent-50 border border-accent-200/60 rounded-xl p-6 md:p-8">
                <p className="text-foreground-800 text-sm md:text-base leading-relaxed">
                  {siteInfo.mission}
                </p>
              </div>
            </div>

            <div className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                发展历程
              </h2>
              <div className="relative pl-6 border-l-2 border-accent-200">
                {milestones.map((item, index) => (
                  <div key={index} className="mb-6 last:mb-0 relative">
                    <div className="absolute -left-[29px] top-1 w-4 h-4 rounded-full bg-accent-500 border-2 border-background-50"></div>
                    <span className="text-accent-600 font-semibold text-sm">{item.year}</span>
                    <p className="text-foreground-700 text-sm mt-1">{item.event}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                合作与信誉
              </h2>
              <p className="text-foreground-600 text-sm mb-6">
                我们与多家权威机构和行业领先企业建立了深度合作关系，确保内容的专业性和权威性。
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {partners.map((partner) => (
                  <div
                    key={partner.name}
                    className="bg-background-100 border border-background-200/70 rounded-xl p-5 text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-building-2-line text-accent-600 text-xl"></i>
                    </div>
                    <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">
                      {partner.name}
                    </h4>
                    <p className="text-foreground-500 text-xs">{partner.role}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                联系方式
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">电子邮箱</h4>
                    <a href={`mailto:${siteInfo.email}`} className="text-foreground-600 text-sm hover:text-accent-600 transition-colors cursor-pointer">{siteInfo.email}</a>
                  </div>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">联系电话</h4>
                    <p className="text-foreground-600 text-sm">{siteInfo.phone}</p>
                  </div>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">办公地址</h4>
                    <p className="text-foreground-600 text-sm">{siteInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                给我们留言
              </h2>
              <form
                onSubmit={handleContactSubmit}
                data-readdy-form
                className="bg-background-100 border border-background-200/70 rounded-xl p-6 md:p-8"
              >
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="about-name" className="block text-foreground-700 text-sm font-medium mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                        placeholder="请输入姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-foreground-700 text-sm font-medium mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                        placeholder="请输入邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-foreground-700 text-sm font-medium mb-1.5">主题</label>
                    <input
                      type="text"
                      id="about-subject"
                      name="subject"
                      required
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-foreground-700 text-sm font-medium mb-1.5">留言</label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors resize-none"
                      placeholder="请输入留言内容（限500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="mobile_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {formStatus === 'success' && (
                    <div className="px-4 py-3 rounded-lg bg-accent-50 border border-accent-200 text-accent-700 text-sm">
                      感谢您的留言！我们将在2个工作日内通过邮件回复您。
                    </div>
                  )}
                  {formStatus === 'error' && formError && (
                    <div className="px-4 py-3 rounded-lg bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full sm:w-auto px-6 py-3 rounded-lg bg-accent-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {formStatus === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : (
                      '发送留言'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}